import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { listInstalledWidgets, updateInstallationConfig, widgetHasSurface, type InstalledWidget } from '../../services/myToolsApi';
import WidgetFrame from './WidgetFrame';
import { withPaused } from './widgetInstallControls';
import { useWidgetInstallsChanged } from './widgetEvents';
import '../../styles/Widgets.css';

const ACTIVE_KEY = 'wg_sidebar_active';

/** Right sidepanel: pick one installed, sidebar-eligible widget and run it in place.
 *  `widgets:open-in-sidebar` (dispatched by WidgetsPage) switches the active widget. */
const WidgetSidebarPanel: React.FC = () => {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState<InstalledWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string>(() => {
    try { return localStorage.getItem(ACTIVE_KEY) ?? ''; } catch { return ''; }
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [resuming, setResuming] = useState(false);
  useWidgetInstallsChanged(() => setReloadKey(k => k + 1));

  useEffect(() => {
    const controller = new AbortController();
    listInstalledWidgets(controller.signal)
      // Paused widgets stay in the list (unlike before) so this panel can show the paused
      // state and offer Resume, instead of the widget just silently disappearing.
      .then(list => { if (!controller.signal.aborted) setWidgets(list.filter(w => widgetHasSurface(w.manifest, 'sidebar'))); })
      .catch(() => { if (!controller.signal.aborted) setWidgets([]); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [reloadKey]);

  const resume = async (widget: InstalledWidget) => {
    setResuming(true);
    try {
      const controls = { ...widget.controls, paused: false };
      const merged = withPaused(widget.rawConfigValues, false);
      await updateInstallationConfig(widget.id, widget.installationId, merged);
      setWidgets(prev => prev.map(w => w.id === widget.id ? { ...w, paused: false, controls, rawConfigValues: merged } : w));
    } catch {
      // Best-effort from this panel; Installed → Configuration always has Resume too.
    } finally {
      setResuming(false);
    }
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ toolId?: string }>).detail?.toolId;
      if (id) setActiveId(String(id));
    };
    window.addEventListener('widgets:open-in-sidebar', handler);
    return () => window.removeEventListener('widgets:open-in-sidebar', handler);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(ACTIVE_KEY, activeId); } catch { /* ignore */ }
  }, [activeId]);

  const active = widgets.find(w => w.id === activeId) ?? widgets[0];

  return (
    <div className="wg-panel">
      <div className="wg-panel__topbar">
        <select
          className="wg-panel__select"
          value={active?.id ?? ''}
          onChange={e => setActiveId(e.target.value)}
          disabled={widgets.length === 0}
          aria-label="Active widget"
        >
          {widgets.length === 0 && <option value="">No sidebar widgets</option>}
          {widgets.map(w => <option key={w.id} value={w.id}>{w.paused ? '⏸ ' : ''}{w.icon} {w.name}</option>)}
        </select>
        <button type="button" className="wg-panel__iconbtn" title="Reload" onClick={() => setReloadKey(k => k + 1)}>
          <FiRefreshCw size={13} />
        </button>
        {active && (
          <button type="button" className="wg-panel__iconbtn" title="Open full size" onClick={() => navigate(`${ROUTES.WIDGETS}/${active.id}`)}>
            <FiExternalLink size={13} />
          </button>
        )}
      </div>

      <div className="wg-panel__view">
        {loading && <div className="wg-panel__empty">Loading widgets…</div>}
        {!loading && !active && (
          <div className="wg-panel__empty">
            <span>No installed widget supports the sidebar yet.</span>
            <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
              Browse Marketplace
            </button>
          </div>
        )}
        {!loading && active && active.paused && (
          <div className="wg-panel__empty">
            <span>⏸ {active.name} is paused.</span>
            <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={resuming} onClick={() => resume(active)}>
              {resuming ? 'Resuming…' : 'Resume widget'}
            </button>
          </div>
        )}
        {!loading && active && !active.paused && (
          <WidgetFrame
            key={`${active.id}-${reloadKey}`}
            toolId={active.id}
            installationId={active.installationId}
            manifest={active.manifest}
            grantedSandbox={active.grantedSandbox}
            config={active.configValues}
            surface="sidebar"
            height={active.manifest?.minHeight ?? 420}
            autoResize
            maxHeight={1200}
          />
        )}
      </div>
    </div>
  );
};

export default WidgetSidebarPanel;
